import { Badge, Table, type Column } from "../../components";
import type { Support } from "../../api/types";
import { t, tDynamic } from "../../i18n/t";

export interface ScoreRow {
  entity: string;
  support: Support;
  source_count: number | null;
  canonical_count: number;
  loaded_count: number | null;
  reason: string | null;
}

const TONE: Readonly<Record<Support, "pass" | "warn" | "fail">> = {
  FULL: "pass",
  PARTIAL: "warn",
  NONE: "fail",
};

/** §B5.3's upper half: support, source count, canonical count, loaded count, reason. */
export function Scorecard({
  rows,
  hideHeading = false,
}: {
  rows: readonly ScoreRow[];
  /** The section it sits in already names it (xmg-actions-first): the heading stays for assistive technology only. */
  hideHeading?: boolean;
}): JSX.Element {
  const columns: readonly Column<ScoreRow>[] = [
    { key: "entity", header: t("dryrun.col_entity"), render: (row) => row.entity },
    {
      key: "support",
      header: t("dryrun.col_support"),
      render: (row) => (
        <Badge tone={TONE[row.support] ?? "neutral"}>{tDynamic("support.", row.support)}</Badge>
      ),
    },
    {
      key: "source_count",
      header: t("dryrun.col_source_count"),
      numeric: true,
      render: (row) => row.source_count ?? t("common.none"),
    },
    {
      key: "canonical_count",
      header: t("dryrun.col_canonical_count"),
      numeric: true,
      render: (row) => row.canonical_count,
    },
    {
      key: "loaded_count",
      header: t("dryrun.col_loaded_count"),
      numeric: true,
      render: (row) => row.loaded_count ?? t("common.none"),
    },
    { key: "reason", header: t("dryrun.col_reason"), render: (row) => row.reason ?? t("common.none") },
  ];
  return (
    <section aria-label={t("dryrun.scorecard")}>
      <h2 className={hideHeading ? "visually-hidden" : undefined}>{t("dryrun.scorecard")}</h2>
      <div className="stacktable">
        <Table
          wrap
          caption={t("dryrun.scorecard")}
          columns={columns}
          rows={rows}
          rowKey={(row) => row.entity}
          emptyMessage={t("dryrun.empty")}
        />
      </div>
    </section>
  );
}
